import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { omit } from 'lodash';
import { Observable, of } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { LocaleInput } from '@app/models';
import { ErrorHandlerService, AuthenticationService, NotyService } from '@app/services';

@Component({
  selector: 'app-email-verify',
  templateUrl: './email-verify.component.html',
  styleUrls: ['./email-verify.component.scss'],
  standalone: false
})
export class EmailVerifyComponent implements OnInit {
  @Input() verifier: string;
  @Input() email: string;
  @Input() password?: string;
  @Input() referral?: string;
  @Input() forceLogin?: boolean;
  @Input() documentLocale?: LocaleInput;
  @Output() verified = new EventEmitter<boolean>();

  otp = new FormControl('');
  isVerifyingToken = false;
  isInvalidToken = false;

  constructor(
    private errorHandlerService: ErrorHandlerService,
    private authenticationService: AuthenticationService,
    private notyService: NotyService,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.email = this.email.toLowerCase();
    this.verifier = this.verifier.toLowerCase();
  }

  submitToken() {
    if (!this.isVerifyingToken) {
      this.isVerifyingToken = true;
      this.isInvalidToken = false;

      if (this.otp.value?.length !== 6) {
        this.isVerifyingToken = false;
        this.isInvalidToken = true;
        return;
      }

      let verify$: Observable<any> = of(null);
      if (this.authenticationService.isAuthenticated() && !this.forceLogin) {
        verify$ = this.authenticationService.verifyEmail({ email: this.email, otp: this.otp.value });
      } else {
        verify$ = this.password
          ? this.authenticationService.completeRegistration({ email: this.email, password: this.password, otp: this.otp.value, ...(this.referral ? { referral: this.referral } : {}) })
          : this.authenticationService.loginEmail({ email: this.email, otp: this.otp.value });
      }

      verify$.subscribe({
        next: (response: any) => {
          this.isVerifyingToken = false;
          this.verified.emit(true);
        },
        error: error => {
          this.isVerifyingToken = false;
          this.isInvalidToken = true;
          this.errorHandlerService.handle(error);
        }
      });
    }
  }

  sendEmailConfirmation() {
    this.isVerifyingToken = true;
    this.isInvalidToken = false;

    this.authenticationService
      .sendEmailVerificationPrompt({
        email: this.email,
        ...(this.documentLocale ? { locale: omit(this.documentLocale, ['__typename']) } : {})
      })
      .pipe(finalize(() => (this.isVerifyingToken = false)))
      .subscribe({
        next: () => {
          this.notyService.success(this.translateService.instant('notyService.confirmationCodeResent'));
          this.authenticationService.clearAllFields();
          this.isInvalidToken = false;
        },
        error: error => this.errorHandlerService.handle(error)
      });
  }

  isOutlookEmail(email: string) {
    return !!(email || '').match(/(@hotmail\.com)|(@outlook\.com)/);
  }
}
